/**
 * useLanguage — app-wide selected language for the v2 experience.
 *
 * The v2 nav exposes a switcher; today only BookDetailPage consumes the value
 * (to render translated book content via /api/translate-book). Other pages
 * will opt-in later. State is persisted to localStorage so a user's choice
 * survives reloads and route changes.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "icraftz.lang.v1";

export const SUPPORTED_LANGUAGES = [
  { code: "en", glyph: "EN", label: "English"   },
  { code: "hi", glyph: "हि",  label: "हिन्दी"     },
  { code: "mr", glyph: "म",   label: "मराठी"     },
  { code: "gu", glyph: "ગુ",  label: "ગુજરાતી"    },
  { code: "kn", glyph: "ಕ",   label: "ಕನ್ನಡ"      },
  { code: "ta", glyph: "த",   label: "தமிழ்"      },
  { code: "te", glyph: "తె",  label: "తెలుగు"     },
  { code: "ml", glyph: "മ",   label: "മലയാളം"    },
  { code: "bn", glyph: "বা",  label: "বাংলা"       },
  { code: "pa", glyph: "ਪ",   label: "ਪੰਜਾਬੀ"      },
];

const VALID_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

const readInitial = () => {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_CODES.has(stored)) return stored;
  } catch (_) { /* localStorage disabled */ }
  return "en";
};

/**
 * Broadcasts language changes across mounted <Navigation/>+BookDetail
 * instances in the same tab. localStorage's native "storage" event only
 * fires cross-tab; we synthesise a same-tab event on set().
 */
const CHANNEL = "icraftz-language-change";

export function useLanguage() {
  const [lang, setLangState] = useState(readInitial);

  useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail?.lang ?? readInitial();
      if (VALID_CODES.has(next)) setLangState(next);
    };
    window.addEventListener(CHANNEL, onChange);
    // Cross-tab sync.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && VALID_CODES.has(e.newValue)) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANNEL, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setLang = useCallback((next) => {
    if (!VALID_CODES.has(next)) return;
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* noop */ }
    setLangState(next);
    window.dispatchEvent(new CustomEvent(CHANNEL, { detail: { lang: next } }));
  }, []);

  return [lang, setLang];
}
